<template>
  <section class="mx-3 lg:mx-0 max-w-screen-2xl">
    <div class="md:mx-10 mx-0">
      <div
        class="w-full flex flex-row justify-between items-center text-root-1 font-normal text-xs md:text-sm"
      >
        <div
          class="justify-start text-root-2 py-4 text-xl w-full flex font-bold"
          id="settingsTourItem4"
        >
          Saved Beneficiaries
        </div>
        <div
          class="bg-primary text-white rounded-xl cursor-pointer py-4 text-xs items-center flex justify-center flex-shrink-0 px-5 gap-2"
          :class="isUserKindViewOnly ? 'opacity-60 cursor-not-allowed' : ''"
          @click="
            isUserKindViewOnly
              ? () => {}
              : openDialog('showAddBeneficiaryDrawer')
          "
        >
          <i class="text-base px-2 el-icon-plus"></i>
          <span>Add Beneficiary</span>
        </div>
      </div>
      <div
        v-if="tenantPermission('asset-access')"
        class="my-6 pb-4 lg:pb-0 border-b"
      >
        <div
          class="flex lg:items-center items-start lg:flex-row flex-col gap-y-3 lg:gap-x-6"
        >
          <div
            @click="makeActive($transactionType.LOCAL_TRANSFER)"
            :class="
              isActive($transactionType.LOCAL_TRANSFER)
                ? 'border-b-2 text-primary font-bold border-primary'
                : 'text-fg-muted font-medium'
            "
            class="cursor-pointer pb-1 lg:pb-2.5 lg:text-13px text-xs max-w-xl"
          >
            Fiat Accounts
          </div>
          <div
            v-if="tenantPermission('asset-access')"
            @click="makeActive($transactionType.ASSET_TRANSFER)"
            :class="
              isActive($transactionType.ASSET_TRANSFER)
                ? 'border-b-2 text-primary font-bold border-primary'
                : 'text-fg-muted font-medium'
            "
            class="cursor-pointer pb-1 lg:pb-2.5 lg:text-13px text-xs max-w-xl"
          >
            Asset Wallets
          </div>
        </div>
      </div>
      <div
        class="w-full border rounded-lg text-root-1 font-normal text-xs md:text-sm bg-center max-height"
      >
        <div v-if="isLoading">
          <beneficiaries-loader />
        </div>
        <template v-else>
          <div
            v-for="(beneficiary, index) in allBeneficiaries"
            :key="index"
            :label="index"
            class="flex px-2.5"
          >
            <div
              v-if="
                beneficiary.transferDestination !==
                  $transactionType.ASSET_TRANSFER && isFiatAccount
              "
              class="flex mb-2 flex-row border-b justify-between items-center w-full py-3"
            >
              <div class="flex md:gap-5 gap-3 items-center">
                <el-avatar
                  :size="60"
                  class="flex items-center justify-center bg-black-2"
                >
                  {{
                    getNameInitials(beneficiary.details.bankDetails.accountName)
                  }}
                </el-avatar>
                <div>
                  <p class="font-bold text-lg capitalize text-root-2">
                    {{ beneficiary.details.bankDetails.accountName }}
                  </p>
                  <div
                    class="flex md:flex-row flex-col md:gap-x-3 md:items-center items-start text-fg-muted mt-2 md:mt-0"
                  >
                    <p class="flex divide-x-2 gap-4">
                      <span>
                        {{ beneficiary.details.bankDetails.bankName }}
                      </span>
                      <span class="pl-4">
                        {{ beneficiary.details.bankDetails.accountNumber }}
                      </span>
                    </p>
                    <p class="flex items-center gap-x-1 mt-2 md:mt-0">
                      <span class="md:mr-1.5 md:ml-3">
                        <currency-icons
                          :currency="
                            getCurrencyCountry(beneficiary.destinationCurrency)
                          "
                        />
                      </span>
                      <span class="font-bold">
                        {{ beneficiary.destinationCurrency }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <div
                class="flex md:pt-0 pt-2.5 gap-2.5 items-center flex-col md:flex-row"
              >
                <div
                  @click="openBeneficiaryDetailsDrawer(beneficiary)"
                  class="text-primary rounded-lg px-2.5 py-2 border flex text-center justify-center"
                >
                  <span class="cursor-pointer">
                    <eye-icon />
                  </span>
                </div>
                <div
                  v-if="hasDepositAccess"
                  @click="deleteBeneficiaryHandler(beneficiary)"
                  class="text-primary rounded-lg px-3 py-2.5 border border-error-default flex text-center justify-center"
                >
                  <span class="cursor-pointer">
                    <delete-icon />
                  </span>
                </div>
              </div>
            </div>
            <div
              v-if="
                beneficiary.transferDestination ===
                  $transactionType.ASSET_TRANSFER &&
                isAssetAccount &&
                tenantPermission('asset-access')
              "
              class="flex md:flex-row flex-col border-b py-4 justify-between items-center w-full"
            >
              <div class="flex gap-x-5 items-center">
                <asset-icon />
                <div>
                  <P class="font-bold text-lg capitalize">
                    {{ beneficiary.details.walletDetails.assetType }}
                  </P>
                  <div
                    class="flex md:flex-row flex-col md:gap-x-5 gap-y-2.5 items-center"
                  >
                    <P class="flex items-center"
                      >Address:
                      <span class="pl-1 flex items-center"
                        >{{
                          maskID(
                            beneficiary.details.walletDetails.walletAddress
                          )
                        }}
                        <span
                          class="cursor-pointer mb-1 ml-2"
                          v-clipboard:copy="
                            beneficiary.details.walletDetails.walletAddress
                          "
                          v-clipboard:success="onCopy"
                          v-clipboard:error="onError"
                        >
                          <copy-icon />
                        </span>
                      </span>
                    </P>
                    <p class="flex items-center gap-x-1.5">
                      Compliance:
                      <span
                        class="text-xs text-center font-normal rounded-lg px-1 py-2 capitalize w-24"
                      >
                        {{ 'N/A' }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <div class="flex md:pt-0 pt-2.5 gap-x-2.5 items-center">
                <div
                  @click="openBeneficiaryDetailsDrawer(beneficiary)"
                  class="text-primary rounded-lg px-2.5 py-2 border flex text-center justify-center"
                >
                  <span class="cursor-pointer">
                    <eye-icon />
                  </span>
                </div>
                <div
                  v-if="hasDepositAccess"
                  @click="deleteBeneficiaryHandler(beneficiary)"
                  class="text-primary rounded-lg px-3 py-2.5 border border-error-default flex text-center justify-center"
                >
                  <span class="cursor-pointer"> <delete-icon /></span>
                </div>
              </div>
            </div>
          </div>
        </template>
        <div
          v-if="isFiatAccount && !hasFiatBeneficiary && !isLoading"
          class="flex flex-col items-center my-5"
        >
          <accounts-empty-state />
          <div
            class="bg-primary text-white rounded-xl cursor-pointer py-4 text-xs items-center flex justify-center flex-shrink-0 px-5 gap-2"
            @click="openDialog('showAddBeneficiaryDrawer')"
          >
            <i class="text-base px-2 el-icon-plus"></i>
            <span>Add Beneficiary</span>
          </div>
        </div>
        <div
          v-if="isAssetAccount && !hasAssetBeneficiary && !isLoading"
          class="flex flex-col items-center my-5"
        >
          <accounts-empty-state />
          <div
            class="bg-primary text-white rounded-xl cursor-pointer py-4 text-xs items-center flex justify-center flex-shrink-0 px-5 gap-2"
            @click="openDialog('showAddBeneficiaryDrawer')"
          >
            <i class="text-base px-2 el-icon-plus"></i>
            <span>Add Beneficiary</span>
          </div>
        </div>
      </div>
      <!-- Endpoint Pagination is not yet ready -->
      <div class="flex justify-between my-6">
        <p class="text-black-10 font-normal">
          Showing {{ startingIndex }} to {{ endingIndex }} of
          {{ pageInfo.totalCount }} entries
        </p>
        <el-pagination
          @current-change="handleCurrentChange"
          :page-size="pageInfo.size"
          background
          layout="prev, pager, next"
          :total="pageInfo.totalCount"
        >
        </el-pagination>
      </div>
    </div>
    <!-- drawers -->
    <beneficiary-details-drawer
      v-if="showDrawer"
      :showDrawer="showDrawer"
      @closeDrawer="closeDrawer"
      :currentBeneficiaryDetails="currentBeneficiaryDetails"
      :activeTab="activeTab"
      @handleDeleteBeneficiary="handleDeleteBeneficiary"
    />
    <withdrawal-account-drawer
      v-if="showWithdrawalAccountDrawer"
      :showDrawer="showWithdrawalAccountDrawer"
      drawerTitle="Add Beneficiary"
      :accountType="accountType"
      @proceedToAddWithdrawal="proceedToAddWithdrawal"
      @closeDrawer="closeDialog('showWithdrawalAccountDrawer')"
    />
    <add-beneficiary
      v-if="showAddBeneficiaryDrawer"
      :showDrawer="showAddBeneficiaryDrawer"
      :type="accountType"
      @closeDrawer="closeDialog('showAddBeneficiaryDrawer')"
      @beneficiarySaved="getBeneficiaries(1, activeTab)"
    />
    <!-- dialog -->
    <attention-dialog
      v-if="showDeleteBeneficiaryDialog"
      :showDialog="showDeleteBeneficiaryDialog"
      :beneficiary="beneficiary"
      @closeDialog="closeDialog('showDeleteBeneficiaryDialog')"
      @deleteBeneficiary="deleteBeneficiary"
      :mode="mode"
      :message="message"
    />
    <verify-auth-dialog
      v-if="showVerifyAuthDialog"
      :authState="withdrawalState"
      :showDialog="showVerifyAuthDialog"
      @closeDialog="closeDialog('showVerifyAuthDialog')"
    />
    <app-tour
      :tourDetails="settingsTour"
      tourComponent="settings"
      :displayTour="displayTour"
      @stopTour="stopTour"
    />
  </section>
</template>

<script>
import { SETTINGS_TOUR } from '@/utils/constants.js'
import tenant from '@/mixins/tenant'
import {
  ParseNetworkName,
  GetCurrencyCountry,
  MaskWalletAddressTwo,
  StatusClass
} from '@/utils/helpers.js'
import userKinds from '@/mixins/userKinds.js'
import terminal from '@/mixins/terminal.js'
import bulkPayout from '@/mixins/bulkPayout.js'

export default {
  mixins: [tenant, userKinds, terminal, bulkPayout],
  components: {
    AppTour: () => import('@/components/v2/app-tour/AppTour.vue'),
    AttentionDialog: () => import('@/components/v2/app/AttentionDialog.vue'),
    AssetIcon: () => import('@/assets/v2/icons/AssetIcon.vue'),
    // PlusIcon: () => import('@/assets/v2/icons/Plus.vue'),
    DeleteIcon: () => import('@/assets/icons/DeleteIcon.vue'),
    EyeIcon: () => import('@/assets/icons/EyeIcon.vue'),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue'),
    BeneficiaryDetailsDrawer: () =>
      import(
        '@/components/v2/settings/drawers/beneficiaryDetailsDrawer/Index.vue'
      ),
    AccountsEmptyState: () =>
      import('@/components/empty-states/AccountsEmptyState.vue'),
    BeneficiariesLoader: () =>
      import('@/components/v2/skeletalLoaders/BeneficiariesLoader.vue'),
    WithdrawalAccountDrawer: () =>
      import(
        '@/components/v2/withdrawal/drawers/withdrawalAccountForm/index.vue'
      ),
    VerifyAuthDialog: () => import('@/components/v2/auth/VerifyAuthDialog.vue'),
    AddBeneficiary: () =>
      import(
        '@/components/v2/settings/drawers/beneficiaryDetailsDrawer/AddBeneficiary.vue'
      )
  },
  data () {
    return {
      // showConfirmationDialog: false,
      // currentTourIndex: 2,
      displayTour: false,
      settingsTour: SETTINGS_TOUR,
      showDrawer: false,
      limitNumber: 5,
      isLoading: false,
      activeTable: this.$transactionType.LOCAL_TRANSFER,
      accountType: 'fiat',
      currentBeneficiaryDetails: {},
      showDeleteBeneficiaryDialog: false,
      showWithdrawalAccountDrawer: false,
      beneficiary: '',
      mode: '',
      message: 'Are you sure you want to delete this account?',
      showVerifyAuthDialog: false,
      withdrawalState: '',
      showAddBeneficiaryDrawer: false,
      payload: {
        page: 1,
        size: 1000
      },
      page: {
        hasNextPage: false,
        hasPrevPage: false,
        page: 0,
        size: 0,
        totalCount: 0
      }
    }
  },
  computed: {
    currentTourIndex () {
      return this.$store.state.appTour.currentSettingsTourIndex
    },
    activeTab () {
      return this.activeTable
    },
    isFiatAccount () {
      return this.activeTable === this.$transactionType.LOCAL_TRANSFER
    },
    isAssetAccount () {
      return this.activeTable === this.$transactionType.ASSET_TRANSFER
    },
    user () {
      return this.$store.state.auth.user
    },
    allBeneficiaries () {
      return this.$store.state.terminal.beneficiaries.items || []
    },
    pageInfo () {
      return this.$store.state.terminal.beneficiaries.page || this.page
    },
    hasAssetBeneficiary () {
      return (
        this.allBeneficiaries.find(
          (item) =>
            item.transferDestination === this.$transactionType.ASSET_TRANSFER
        ) && this.isAssetAccount
      )
    },
    hasFiatBeneficiary () {
      return this.allBeneficiaries.find(
        (item) =>
          item.transferDestination === this.$transactionType.FX_TRANSFER ||
          item.transferDestination === this.$transactionType.LOCAL_TRANSFER
      )
    },
    startingIndex () {
      return (this.pageInfo.page - 1) * this.pageInfo.size + 1
    },
    endingIndex () {
      const endIndex = this.pageInfo.page * this.pageInfo.size
      return endIndex > this.pageInfo.totalCount
        ? this.pageInfo.totalCount
        : endIndex
    }
  },
  methods: {
    startSettingsTour () {
      this.$tours.SettingsTour.start(this.currentTourIndex)
      this.displayTour = true
    },
    stopTour () {
      this.displayTour = false
      this.$store.dispatch('appTour/updateSettingsTourStatus', false)
    },
    handleDeleteBeneficiary (beneficiary = this.currentBeneficiaryDetails) {
      this.deleteBeneficiaryHandler(beneficiary)
      this.closeDrawer()
    },
    closeDialog (arg) {
      this[arg] = false
    },
    openDialog (arg) {
      this[arg] = true
    },
    openBeneficiaryDetailsDrawer (beneficiary) {
      this.currentBeneficiaryDetails = beneficiary
      this.showDrawer = true
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    maskID (val) {
      return MaskWalletAddressTwo(val)
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Copied'
      })
    },
    closeDrawer () {
      this.showDrawer = false
    },
    isActive (tab) {
      if (tab === this.activeTable) {
        return true
      } else {
        return false
      }
    },
    async makeActive (tab) {
      this.activeTable = tab
      this.accountType = tab === 'local_transfer' ? 'fiat' : 'asset'
      this.isLoading = true
      await this.getBeneficiaries(1, this.activeTable) // reset the current page to 1
      this.isLoading = false
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    statusClass (status) {
      return StatusClass(status)
    },
    showMore () {
      this.limitNumber = this.limitNumber + 10
    },
    showBeneficiaryDetails (beneficiary) {
      this.$emit('showBeneficiaryDetails', beneficiary)
    },
    deleteBeneficiaryHandler (beneficiary) {
      this.beneficiary = beneficiary.id
      this.mode = 'deleteBeneficiary'
      this.showDeleteBeneficiaryDialog = true
    },
    deleteBeneficiary (id) {
      this.isLoading = true
      this.$store
        .dispatch('terminal/deleteBeneficiary', id)
        .then(() => {
          this.$swal({
            icon: 'success',
            title: 'Beneficiary Deleted',
            html: 'This beneficiary has been deleted successfully!',
            iconColor: 'var(--color-primary)',
            showCloseButton: true
          })
          this.getBeneficiaries(1, this.activeTable)
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    parseNetworkName (networkType) {
      return ParseNetworkName(networkType)
    },
    proceedToAddWithdrawal (payload) {
      this.withdrawalDetails = payload
      this.verifyAuth('createWithdrawal')
    },
    verifyAuth (withdrawalState) {
      this.showVerifyAuthDialog = true
      this.withdrawalState = withdrawalState
    },
    handleCurrentChange (val) {
      this.isLoading = true
      this.getBeneficiaries(val, this.activeTable, 5)
      this.isLoading = false
    }
  },
  async mounted () {
    this.isLoading = true
    await this.getBeneficiaries(1, this.activeTable, 5)
    this.isLoading = false
    if (this.$store.state.appTour.settingsTourIsActive) {
      this.startSettingsTour()
    }
  }
}
</script>

<style scoped>
.beneficiary {
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.1),
    0px 4px 8px rgba(3, 2, 7, 0.1);
  border-radius: 15px;
}
.max-height {
  max-height: 60vh;
  overflow-y: scroll;
}
.scrollbar {
  overflow-y: scroll;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
}
.scrollbar::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}
>>> .el-pagination.is-background .btn-next.disabled,
.el-pagination.is-background .btn-next:disabled,
.el-pagination.is-background .btn-prev.disabled,
.el-pagination.is-background .btn-prev:disabled,
.el-pagination.is-background .el-pager li.disabled {
  background: green !important;
}
>>> .el-pagination.is-background .btn-next,
.el-pagination.is-background .btn-prev,
.el-pagination.is-background .el-pager li {
  background: transparent !important;
  border: 0.941331px solid rgba(207, 219, 213, 0.7);
  border-radius: 7.53065px;
}
>>> .el-pagination.is-background .btn-prev,
.el-pagination.is-background .btn-next,
.el-pagination.is-background .el-pager li {
  background: transparent !important;
  border: 0.941331px solid rgba(207, 219, 213, 0.7);
  border-radius: 7.53065px;
}
>>> li.number.active {
  background-color: var(--color-primary) !important;
  border: none !important;
  color: white !important;
  border-radius: 7.53065px !important;
}
>>> li.number {
  border: none !important;
  font-weight: 500;
  font-size: 12px;
  color: var(--text-2);
  background: transparent !important;
  /* color: var(--color-primary) !important ; */
}
>>> li.number:hover {
  border: 0.941331px solid var(--color-primary) !important;
  border-radius: 7.53065px !important;
  color: var(--text-2) !important;
}
</style>
